import { Card, CardContent, Typography } from '@material-ui/core';
import { LocationOn } from '@material-ui/icons';
import { FormattedDate } from '@superdispatch/dates';
import {
  ColorDynamic,
  OverflowText,
  Stack,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { useDriverAppSettings } from 'carrier/settings/data/SettingsAPI';
import { useEffect, useRef, useState } from 'react';
import { TEN_MINUTES } from 'shared/constants/NumberConstants';
import { WarningTriangleIcon } from 'shared/icons/WarningTriangleIcon';
import { useFlag } from 'shared/settings/FeatureToggles';
import { CopyToClipboardTooltip } from 'shared/ui/CopyToClipboardTooltip';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from 'shared/ui/Timeline';
import styled from 'styled-components';
import { LoadActivityDTO, LoadDTO } from '../data/LoadDTO';

const LocationRangeText = styled(Typography)`
  &[data-has-hover-text='true'] {
    border-bottom: 1px dashed ${ColorDynamic.Silver500};
    cursor: pointer;
  }
  &[data-has-warning='true'] {
    color: ${ColorDynamic.Red500};
    border-color: 1px dashed ${ColorDynamic.Red500};
  }
`;

interface ViewLoadActivityProps {
  load: LoadDTO;
  changedByBrokerActivities: LoadActivityDTO;
}

export function ViewLoadActivity({
  load,
  changedByBrokerActivities,
}: ViewLoadActivityProps) {
  const isGatePassEnabled = useFlag('ctms_gate_pass');
  const [activeLogs, setActiveLogs] = useState<LoadActivityDTO>(
    changedByBrokerActivities,
  );
  const ref = useRef<HTMLUListElement>(null);
  const isMobile = useResponsiveValue(true, false);
  const { data: driverAppSettings } = useDriverAppSettings({
    staleTime: TEN_MINUTES,
  });

  const isUpdated =
    load.activities?.some((item) => item.is_changed_by_broker) ||
    load.is_changed_by_broker;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (isUpdated && !load.archived && load.is_active && activeLogs) {
      const timeout = activeLogs.length <= 3 ? 5000 : 8000;
      timeoutId = setTimeout(() => {
        setActiveLogs([]);
      }, timeout);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isUpdated, load.archived, load.is_active, activeLogs]);

  useEffect(() => {
    if (isUpdated && !load.archived && load.is_active) {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isUpdated, load.archived, load.is_active]);

  return (
    <Card aria-label="Activity Timeline">
      <CardContent>
        <Stack space="small">
          <Typography variant="h3" color="textPrimary" id="activity-timeline">
            Activity
          </Typography>

          <Timeline ref={ref}>
            {load.activities?.map(
              (
                { timestamp, user, text, level, subtext, action_type },
                idx,
                activities,
              ) => {
                const isLastElement = idx === activities.length - 1;
                const isNotInRange =
                  driverAppSettings?.arrived_at_location_min_range_in_miles &&
                  subtext?.away_from_location_in_miles &&
                  subtext.away_from_location_in_miles >
                    driverAppSettings?.arrived_at_location_min_range_in_miles;

                const isGatePassActivity =
                  action_type === 'protected_attachment_accessed_at_pickup' &&
                  isGatePassEnabled;
                const hasWarning =
                  (isNotInRange || !subtext?.away_from_location_in_miles) &&
                  !isGatePassActivity;

                const activityText = text.includes('\n')
                  ? text.split('\n')
                  : [text];

                return (
                  <TimelineItem aria-label={`activity-item-${idx}`} key={idx}>
                    <TimelineSeparator>
                      <TimelineDot
                        aria-label={`activity-timeline-dot-${idx}`}
                        color={level}
                      />
                      {!isLastElement && <TimelineConnector />}
                    </TimelineSeparator>

                    <TimelineContent
                      isUnRead={
                        activeLogs?.find(
                          (item) => item.timestamp === timestamp,
                        ) &&
                        !load.archived &&
                        load.is_active
                      }
                    >
                      <Box
                        paddingBottom={
                          !isLastElement && !isMobile ? 'small' : 'none'
                        }
                      >
                        <Stack space="xxsmall">
                          {activityText.map((line, index) => {
                            return (
                              <TextBox
                                wrapOverflow={true}
                                variant={index === 0 ? 'body-semibold' : 'body'}
                              >
                                {line}
                              </TextBox>
                            );
                          })}

                          <Stack space="xxsmall">
                            {timestamp && (
                              <Typography
                                component="p"
                                variant="caption"
                                color="textSecondary"
                              >
                                <FormattedDate
                                  date={timestamp}
                                  variant="DateTime"
                                />
                              </Typography>
                            )}

                            {subtext?.text && (
                              <Box display="flex" alignItems="center">
                                <Box paddingRight="xxsmall">
                                  {hasWarning ? (
                                    <WarningTriangleIcon fontSize="small" />
                                  ) : (
                                    <LocationOn
                                      fontSize="small"
                                      color="action"
                                    />
                                  )}
                                </Box>
                                <CopyToClipboardTooltip
                                  textToCopy={subtext.hover_text || ''}
                                  title={subtext.hover_text || ''}
                                >
                                  <LocationRangeText
                                    variant="caption"
                                    color="textSecondary"
                                    noWrap={true}
                                    data-has-warning={hasWarning}
                                    data-has-hover-text={!!subtext.hover_text}
                                  >
                                    {subtext.text}
                                  </LocationRangeText>
                                </CopyToClipboardTooltip>
                              </Box>
                            )}

                            {user?.email && (
                              <OverflowText
                                component="p"
                                variant="caption"
                                color="textSecondary"
                              >
                                {user.email}
                              </OverflowText>
                            )}
                          </Stack>
                        </Stack>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                );
              },
            )}
          </Timeline>
        </Stack>
      </CardContent>
    </Card>
  );
}
